/**
 * TenantContext — University branding for the current subdomain.
 *
 * Fetched once at app boot via plain fetch() (not apiClient, which
 * hasn't initialised yet at that point).  Provides:
 *
 *   { university, subdomain, isTenant, brandLoading }
 *
 * - university : the full university object returned by
 *               GET /api/universities/subdomain/:sub, or null
 * - subdomain  : string slug (e.g. "harvard"), or null on root domain
 * - isTenant   : true when on a university subdomain
 * - brandLoading: true while the fetch is in flight
 */

import React, { createContext, useContext, useEffect, useState } from "react";
import { detectSubdomain } from "../utils/subdomain";

const TenantContext = createContext({
  university: null,
  subdomain: null,
  isTenant: false,
  brandLoading: false,
});

export const TenantProvider = ({ children }) => {
  const subdomain = detectSubdomain();
  const isTenant  = subdomain !== null;

  const [university,   setUniversity]   = useState(null);
  const [brandLoading, setBrandLoading] = useState(isTenant);

  useEffect(() => {
    if (!subdomain) return;

    // Resolve the API root: honour the env var; default to same-origin /api
    const apiRoot =
      process.env.REACT_APP_API_URL
        ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "") + "/api"
        : "/api";

    fetch(`${apiRoot}/universities/subdomain/${subdomain}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (body?.success && body?.data) setUniversity(body.data);
      })
      .catch(() => {})
      .finally(() => setBrandLoading(false));
  }, [subdomain]);

  return (
    <TenantContext.Provider value={{ university, subdomain, isTenant, brandLoading }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => useContext(TenantContext);

export default TenantContext;
